import { useId } from "react";
import { Box, MenuItem, Pagination, TextField, Typography } from "@mui/material";

/** `pageSize` for "show every row on one page". */
export const ALL_ROWS = -1;

/**
 * A list's pager: "Showing 26–50 of 572 doctors", numbered pages with first
 * and last, and (unless `compact`) a rows-per-page picker. `page` is 1-based.
 * Lists show the full bar above the table and a compact one below it.
 */
const ListPagination = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  noun = "rows",
  summary,
  compact = false,
  testId,
}) => {
  const id = useId();
  const showAll = pageSize === ALL_ROWS;
  const pageCount = showAll ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const from = total === 0 ? 0 : showAll ? 1 : (current - 1) * pageSize + 1;
  const to = showAll ? total : Math.min(total, current * pageSize);
  const range =
    total === 0
      ? `No ${noun}`
      : `Showing ${from.toLocaleString("en-IN")}–${to.toLocaleString("en-IN")} of ${total.toLocaleString("en-IN")} ${noun}`;

  return (
    <Box
      data-testid={testId}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        py: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary" aria-live="polite">
        {summary ? summary({ from, to, range }) : range}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
        {!compact && onPageSizeChange && (
          <TextField
            select
            id={`${id}-size`}
            size="small"
            label="Rows per page"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            sx={{ minWidth: 130 }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === ALL_ROWS ? "All" : option}
              </MenuItem>
            ))}
          </TextField>
        )}
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={current}
            onChange={(event, value) => onPageChange(value)}
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            color="primary"
            shape="rounded"
            size="small"
          />
        )}
      </Box>
    </Box>
  );
};

export default ListPagination;
